import {
  Stack, Text, Group, Button, Card, Badge, ThemeIcon, Divider, Alert, Tooltip,
} from '@mantine/core'
import {
  IconShieldCheck, IconRefresh, IconKey, IconUserOff, IconTrash, IconAlertTriangle,
  IconShieldOff, IconRotateClockwise,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useDisclosure } from '@mantine/hooks'
import { api } from '@/api/client'
import { mfaApi } from '@/api/mfa'
import { useAuthStore } from '@/store/auth'
import { AdminResetTotpModal } from './AdminResetTotpModal'
import { UserDeleteModal } from './UserDeleteModal'
import type { UserRow } from './UsersTable'

interface UserDrawerSecurityProps {
  user: UserRow
  onClose: () => void
}

function kanidmStatus(user: UserRow): { label: string; color: string } {
  if (user.must_change_password) return { label: 'Aguardando troca', color: 'yellow' }
  if (user.ldap_synced === true) return { label: 'Sincronizado', color: 'teal' }
  return { label: 'Pendente', color: 'orange' }
}

export function UserDrawerSecurity({ user: targetUser, onClose }: UserDrawerSecurityProps) {
  const qc = useQueryClient()
  const { user } = useAuthStore()

  const [resetLink, setResetLink] = useState<string | null>(null)
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false)
  const [deactivateConfirm, setDeactivateConfirm] = useState(false)

  const status = kanidmStatus(targetUser)

  const syncLdapMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/v1/users/${targetUser.id}/sync-ldap`, {})
      return res.data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['users'] })
      notifications.show({ color: 'teal', title: 'Sincronizado', message: 'Usuário sincronizado com Kanidm.' })
      if (data?.reset_token) {
        setResetLink(data.reset_token)
      }
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: err?.response?.data?.detail ?? 'Falha ao sincronizar.' })
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/v1/users/${targetUser.id}/reset-password`, {})
      return res.data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['users'] })
      if (data?.reset_token || data?.reset_link) {
        setResetLink(data.reset_token ?? data.reset_link)
        notifications.show({ color: 'teal', title: 'Token gerado', message: 'Link de reset válido por 24h.' })
      } else {
        notifications.show({ color: 'teal', title: 'Reset enviado', message: 'Usuário precisará trocar a senha.' })
      }
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: err?.response?.data?.detail ?? 'Falha ao gerar reset.' })
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: async () => {
      await api.put(`/v1/users/${targetUser.id}`, { is_active: !targetUser.is_active })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setDeactivateConfirm(false)
      notifications.show({
        color: targetUser.is_active ? 'yellow' : 'teal',
        title: targetUser.is_active ? 'Desativado' : 'Ativado',
        message: `Usuário ${targetUser.is_active ? 'desativado' : 'ativado'} com sucesso.`,
      })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: err?.response?.data?.detail ?? 'Falha ao alterar status.' })
    },
  })

  const [resetTotpOpened, { open: openResetTotp, close: closeResetTotp }] = useDisclosure(false)

  const securityQuery = useQuery({
    queryKey: ['user-security', targetUser.id],
    queryFn: () => mfaApi.getUserSecurity(targetUser.id),
    enabled: !!targetUser?.id,
    retry: 1,
  })

  const resetTotpMutation = useMutation({
    mutationFn: () => mfaApi.adminResetTotp(targetUser.id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['user-security', targetUser.id] })
      qc.invalidateQueries({ queryKey: ['users'] })
      notifications.show({
        color: 'teal', title: 'TOTP resetado',
        message: `${data.sessions_revoked} sessao(oes) encerrada(s). Usuario notificado por email${data.notified.whatsapp ? ' e WhatsApp' : ''}.`,
      })
      closeResetTotp()
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail
      if (detail === 'no_totp_to_reset') {
        notifications.show({
          color: 'yellow', title: 'Sem TOTP',
          message: 'Esse usuário não tem TOTP cadastrado.',
        })
      } else if (detail === 'use_self_service_endpoint') {
        notifications.show({
          color: 'yellow', title: 'Use /perfil',
          message: 'Você não pode resetar seu próprio TOTP por aqui.',
        })
      } else {
        notifications.show({
          color: 'red', title: 'Erro',
          message: err?.response?.data?.detail ?? 'Falha ao resetar TOTP.',
        })
      }
    },
  })

  const isSelfTarget = user?.email === targetUser.email
  const hasTotp = securityQuery.data?.has_totp ?? false
  const totpStatusLabel = securityQuery.isLoading
    ? '—'
    : (hasTotp ? 'Ativo' : 'Não configurado')
  const resetButtonDisabled =
    securityQuery.isLoading || !hasTotp || isSelfTarget
  const resetButtonTooltip = isSelfTarget
    ? 'Use /perfil para gerenciar seu próprio TOTP'
    : (!hasTotp && !securityQuery.isLoading)
      ? 'Usuário não tem TOTP cadastrado'
      : ''

  return (
    <Stack gap="md">
      {/* Kanidm status */}
      <Card shadow="xs" withBorder p="sm">
        <Group gap="xs" mb="xs">
          <ThemeIcon size="sm" variant="light" color="teal"><IconShieldCheck size={14} /></ThemeIcon>
          <Text size="sm" fw={600}>Kanidm / LDAP</Text>
        </Group>
        <Group justify="space-between" mb="sm">
          <Text size="sm" c="dimmed">Status:</Text>
          <Badge color={status.color} variant="light" size="sm">{status.label}</Badge>
        </Group>
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconRefresh size={14} />}
            loading={syncLdapMutation.isPending}
            onClick={() => syncLdapMutation.mutate()}
          >
            Re-sync com Kanidm
          </Button>
          <Button
            size="xs"
            variant="light"
            color="yellow"
            leftSection={<IconKey size={14} />}
            loading={resetPasswordMutation.isPending}
            onClick={() => resetPasswordMutation.mutate()}
          >
            Forçar reset de senha
          </Button>
        </Group>

        {resetLink && (
          <Alert color="teal" mt="sm" title="Link de reset (válido por 24h)">
            <Text size="xs" style={{ wordBreak: 'break-all' }}>{resetLink}</Text>
          </Alert>
        )}
      </Card>

      {/* MFA / TOTP */}
      <Card shadow="xs" withBorder p="sm">
        <Group gap="xs" mb="xs">
          <ThemeIcon size="sm" variant="light" color={hasTotp ? 'teal' : 'gray'}>
            {hasTotp ? <IconShieldCheck size={14} /> : <IconShieldOff size={14} />}
          </ThemeIcon>
          <Text size="sm" fw={600}>MFA / TOTP</Text>
        </Group>
        <Group justify="space-between" mb="sm">
          <Text size="sm" c="dimmed">Status:</Text>
          <Badge color={hasTotp ? 'teal' : 'gray'} variant="light" size="sm">
            {totpStatusLabel}
          </Badge>
        </Group>
        <Tooltip label={resetButtonTooltip} disabled={!resetButtonTooltip}>
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconRotateClockwise size={14} />}
            disabled={resetButtonDisabled}
            onClick={openResetTotp}
          >
            Resetar TOTP
          </Button>
        </Tooltip>
      </Card>

      <AdminResetTotpModal
        opened={resetTotpOpened}
        targetEmail={targetUser.email}
        isPending={resetTotpMutation.isPending}
        onConfirm={() => resetTotpMutation.mutate()}
        onCancel={closeResetTotp}
      />

      {/* Danger zone */}
      <Card
        shadow="xs"
        p="sm"
        style={{ border: '1px solid var(--mantine-color-red-filled)', background: 'var(--mantine-color-red-light)' }}
      >
        <Group gap="xs" mb="sm">
          <ThemeIcon size="sm" variant="light" color="red"><IconAlertTriangle size={14} /></ThemeIcon>
          <Text size="sm" fw={600} c="red">Zona de perigo</Text>
        </Group>

        {/* Deactivate */}
        {!deactivateConfirm ? (
          <Button
            size="xs"
            variant="light"
            color={targetUser.is_active ? 'yellow' : 'teal'}
            leftSection={<IconUserOff size={14} />}
            onClick={() => setDeactivateConfirm(true)}
            fullWidth
            mb="xs"
          >
            {targetUser.is_active ? 'Desativar usuário' : 'Reativar usuário'}
          </Button>
        ) : (
          <Card withBorder p="xs" mb="xs">
            <Text size="xs" mb="xs">
              Tem certeza que deseja {targetUser.is_active ? 'desativar' : 'reativar'} <strong>{targetUser.full_name}</strong>?
            </Text>
            <Group gap="xs">
              <Button
                size="xs"
                color={targetUser.is_active ? 'yellow' : 'teal'}
                loading={toggleActiveMutation.isPending}
                onClick={() => toggleActiveMutation.mutate()}
              >
                Confirmar
              </Button>
              <Button size="xs" variant="default" onClick={() => setDeactivateConfirm(false)}>
                Cancelar
              </Button>
            </Group>
          </Card>
        )}

        <Divider my="xs" />

        {/* Remove user (anonymize) */}
        <Button
          size="xs"
          color="red"
          variant="light"
          leftSection={<IconTrash size={14} />}
          onClick={openDelete}
          fullWidth
        >
          Remover usuário
        </Button>
      </Card>

      <UserDeleteModal
        opened={deleteOpened}
        onClose={closeDelete}
        user={targetUser}
        onDeleted={onClose}
      />
    </Stack>
  )
}
